import { useId, type CSSProperties } from 'react';

import { focusRing, Icon } from '@/design-system';
import type { ReadingGuideJudgement } from '@/entities/publicReport';
import { cn } from '@/shared/lib';

import type { PublicCopy } from '../copy';
import { isModifiedClick } from '../jump';
import type { OutlineItem } from '../reportIndex';
import { chapterAnchor, outlineRowId } from '../useReportView';
import { JudgementPill } from './JudgementPill';
import { SectionTitle } from './SectionTitle';

export interface ReportOutlineProps {
    items: OutlineItem[];
    /** De slotregel over onderdelen zonder bijzonderheden en wat niet gekeurd is. */
    note: string | null;
    copy: PublicCopy;
    headingLevel: 2 | 3;
    /** Naar dit onderdeel of deze kop in het rapport. */
    onJump: (key: string) => void;
    /**
     * De oordelen uit "Zo leest u dit rapport", in hun kleur, en het anker van
     * dat hoofdstuk: boven de lijst, zodat de koper de kleuren kent vóór het
     * eerste oordeel. `null` zonder dat hoofdstuk.
     */
    guide?: { key: string; judgements: ReadingGuideJudgement[] } | null;
}

/**
 * Elk onderdeel van het rapport onder elkaar, met de koppen eronder ingesprongen
 * - hoofdstuk, sectie, en wat daaronder hangt - en rechts het oordeel in woorden
 * ("Matig", nooit een getal) in de kleur van dat oordeel, dezelfde als in het
 * rapport. Een onderdeel zonder oordeel, zoals de toelichting, krijgt geen label.
 *
 * Elke regel is een link naar zijn plek in het rapport (`#rapport-6-2`): een tik
 * opent het tabblad Rapport daar. Kopiëren en een nieuw tabblad werken ook.
 *
 * Boven de lijst staan de oordelen in hun kleur, met een link naar "Zo leest u
 * dit rapport": de koper ziet wat de kleuren zijn voordat hij het eerste oordeel
 * leest, en leest met één tik wat ze betekenen.
 */
export function ReportOutline({
    items,
    note,
    copy,
    headingLevel,
    onJump,
    guide = null,
}: ReportOutlineProps) {
    const titleId = useId();

    if (items.length === 0) {
        return null;
    }

    return (
        <nav
            aria-labelledby={titleId}
            className="border-b border-[var(--border-subtle)] px-[var(--pr-pad-x)] py-6"
            /* De stap per niveau groeit mee met de pagina: smal krap, breed ruim. */
            style={{ '--outline-step': 'clamp(14px, 3.2cqi, 24px)' } as CSSProperties}
        >
            <SectionTitle level={headingLevel} id={titleId} description={copy.outlineIntro}>
                {copy.partsTitle}
            </SectionTitle>

            {guide && guide.judgements.length > 0 ? (
                <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2">
                    <ul
                        aria-label={copy.judgementLegend}
                        className="m-0 flex list-none flex-wrap items-center gap-[6px] p-0"
                    >
                        {guide.judgements.map((judgement) => (
                            <li key={judgement.key} className="m-0">
                                <JudgementPill label={judgement.label} tone={judgement.tone} />
                            </li>
                        ))}
                    </ul>
                    <a
                        href={`#${chapterAnchor(guide.key)}`}
                        onClick={(event) => {
                            if (isModifiedClick(event)) {
                                return;
                            }

                            event.preventDefault();
                            onJump(guide.key);
                        }}
                        className={cn(
                            'inline-flex min-h-[44px] items-center gap-1 rounded-[6px] px-1 text-[13px] leading-[1.3] font-medium text-[var(--accent-dark)] no-underline hover:text-[var(--accent)] hover:underline',
                            focusRing,
                        )}
                    >
                        {copy.judgementLegendLink}
                        <Icon name="chevronDown" size="sm" className="-rotate-90" />
                    </a>
                </div>
            ) : null}

            <div className="-mx-2 border-b border-[var(--border-subtle)]">
                <OutlineList items={items} depth={0} onJump={onJump} />
            </div>

            {note ? (
                <p className="m-0 mt-3 text-[12px] leading-[1.45] text-[var(--text-tertiary-safe)]">
                    {note}
                </p>
            ) : null}
        </nav>
    );
}

function OutlineList({
    items,
    depth,
    onJump,
}: {
    items: OutlineItem[];
    depth: number;
    onJump: (key: string) => void;
}) {
    return (
        <ol className="m-0 list-none p-0">
            {items.map((item) => (
                <li key={item.key} className="m-0">
                    <a
                        id={outlineRowId(item.key)}
                        href={`#${chapterAnchor(item.key)}`}
                        data-jump-row=""
                        onClick={(event) => {
                            if (isModifiedClick(event)) {
                                return;
                            }

                            event.preventDefault();
                            onJump(item.key);
                        }}
                        style={{ paddingLeft: `calc(8px + ${depth} * var(--outline-step))` }}
                        className={cn(
                            'flex min-h-[44px] scroll-mt-[64px] items-center gap-3 border-t py-[10px] pr-2 no-underline',
                            depth === 0
                                ? 'border-[var(--border-subtle)]'
                                : 'border-[var(--border-subtle-2)]',
                            'transition-colors duration-100 hover:bg-[var(--surface-row-hover)] data-[jumped]:bg-[var(--accent-tint)]',
                            focusRing,
                            'focus-visible:-outline-offset-2',
                        )}
                    >
                        <span
                            className={cn(
                                'flex min-w-0 flex-1 items-baseline gap-2 leading-[1.35] text-pretty',
                                depth === 0
                                    ? 'text-[15px] font-medium text-[var(--text-primary)]'
                                    : depth === 1
                                      ? 'text-[14px] text-[var(--text-primary)]'
                                      : 'text-[14px] text-[var(--text-secondary)]',
                            )}
                        >
                            {item.number ? (
                                <span
                                    className={cn(
                                        'shrink-0 font-[family-name:var(--font-mono)] text-[12px] font-normal text-[var(--text-muted-mono-safe)]',
                                        depth === 0 && 'min-w-[1.75rem]',
                                    )}
                                >
                                    {item.number}
                                </span>
                            ) : null}
                            <span className="min-w-0 break-words">{item.title}</span>
                        </span>

                        {item.judgement ? (
                            <JudgementPill
                                label={item.judgement}
                                tone={item.tone}
                                className="shrink-0"
                            />
                        ) : null}

                        <Icon
                            name="chevronDown"
                            size="sm"
                            tone="muted"
                            className="shrink-0 -rotate-90"
                        />
                    </a>

                    {item.children.length > 0 ? (
                        <OutlineList items={item.children} depth={depth + 1} onJump={onJump} />
                    ) : null}
                </li>
            ))}
        </ol>
    );
}
